// dashboard/src/components/dashboard/api-keys/CreateApiKeyModal.tsx
import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ApiKeyCreateResponse } from '../../../services/apiKeyService';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateApiKey: (keyName: string) => Promise<ApiKeyCreateResponse>;
  isLoading: boolean;
}

const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  isOpen,
  onClose,
  onCreateApiKey,
  isLoading,
}) => {
  const [keyName, setKeyName] = useState('');
  const [newApiKey, setNewApiKey] = useState<ApiKeyCreateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await onCreateApiKey(keyName);
      setNewApiKey(response);
      setKeyName('');
    } catch (err: any) {
      setError(err.message || 'Failed to create API key');
    }
  };

  const handleClose = () => {
    setKeyName('');
    setNewApiKey(null);
    setError(null);
    onClose();
  };

  const copyToClipboard = () => {
    if (newApiKey) {
      navigator.clipboard.writeText(newApiKey.api_key);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={handleClose}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={handleClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {newApiKey ? 'API Key Created' : 'Create New API Key'}
                  </Dialog.Title>

                  {error && (
                    <div className="mt-2 rounded-md alert-error p-4">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-error-800">Error</h3>
                          <div className="mt-2 text-sm text-error-700">
                            <p>{error}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!newApiKey ? (
                    <form onSubmit={handleSubmit} className="mt-4">
                      <div>
                        <label htmlFor="key-name" className="block text-sm font-medium text-gray-700">
                          Key Name (optional)
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="key-name"
                            id="key-name"
                            className="input-field"
                            placeholder="My API Key"
                            value={keyName}
                            onChange={(e) => setKeyName(e.target.value)}
                          />
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Give your API key a name to help you identify it later.
                        </p>
                      </div>
                      <div className="mt-5 sm:mt-6">
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="btn-primary w-full"
                        >
                          {isLoading ? 'Creating...' : 'Create API Key'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="mt-4">
                      <div className="rounded-md alert-warning p-4">
                        <div className="flex">
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-warning-800">Important</h3>
                            <div className="mt-2 text-sm text-warning-700">
                              <p>
                                This API key will only be displayed once. Please copy it and store it securely.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <label htmlFor="api-key" className="block text-sm font-medium text-gray-700">
                          Your API Key
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm">
                          <div className="relative flex items-stretch flex-grow focus-within:z-10">
                            <input
                              type="text"
                              name="api-key"
                              id="api-key"
                              className="input-field rounded-none rounded-l-md"
                              value={newApiKey.api_key}
                              readOnly
                            />
                          </div>
                          <button
                            type="button"
                            onClick={copyToClipboard}
                            className="btn-secondary rounded-none rounded-r-md -ml-px"
                          >
                            <span>Copy</span>
                          </button>
                        </div>
                      </div>

                      <div className="mt-5 sm:mt-6">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="btn-primary w-full"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CreateApiKeyModal;
